/**
 * Pure funnel-routing for the builder's FIRST step — the prebuilt-vs-custom
 * picker (memory `monetization-onboarding-funnel`, docs/idea/companions.md). The
 * owner-specified v1 funnel: picking a PREBUILT companion is the free front door
 * (→ account setup, then limited free chat + a pre-generated gallery), while a
 * CUSTOM companion is paid upfront (→ billing IMMEDIATELY, before any build).
 *
 * This is the JSX-free decision half so the routing is unit-tested without a DOM;
 * `BuilderPicker.tsx` renders the two doors and calls back with the choice. No
 * network, no catalog fetch, no domain logic: the picker only decides which door
 * the user walks through — the free path or the paywall.
 */

/** The two funnel doors the picker offers. */
export type BuilderChoice = 'prebuilt' | 'custom';

/**
 * Where a choice sends the user next:
 *  - `catalog` — the free prebuilt path: the ready-made companion CATALOG, which
 *    provisions by cloning a template (no generation job, no charge). It must NOT
 *    land on the custom describe/upload form — that form ends in a 30-credit
 *    reference build that `402`s a fresh $0 account, which turns the free front
 *    door into a dead end.
 *  - `billing` — the paid custom path: payment IMMEDIATELY, before any build.
 */
export type BuilderDestination = 'catalog' | 'billing';

/** Presentation metadata for one door — every label is an i18n key, never copy. */
export interface BuilderChoiceOption {
  readonly choice: BuilderChoice;
  /** i18n key for the card title. */
  readonly titleKey: string;
  /** i18n key for the card blurb. */
  readonly blurbKey: string;
  /** i18n key for the free/premium badge. */
  readonly badgeKey: string;
  /** i18n key for the card's call-to-action button. */
  readonly ctaKey: string;
  /** True for the free front door (prebuilt) — drives badge + button styling. */
  readonly free: boolean;
}

/** The doors in funnel order — the free front door (prebuilt) first. */
export const BUILDER_CHOICES: readonly BuilderChoiceOption[] = [
  {
    choice: 'prebuilt',
    titleKey: 'builder.picker.prebuilt.title',
    blurbKey: 'builder.picker.prebuilt.blurb',
    badgeKey: 'builder.picker.free',
    ctaKey: 'builder.picker.prebuilt.cta',
    free: true,
  },
  {
    choice: 'custom',
    titleKey: 'builder.picker.custom.title',
    blurbKey: 'builder.picker.custom.blurb',
    badgeKey: 'builder.picker.paid',
    ctaKey: 'builder.picker.custom.cta',
    free: false,
  },
];

/**
 * Route a picker choice to its next destination. Custom generation is paid
 * upfront, so it goes to BILLING immediately; prebuilt is the free start, so it
 * goes to the ready-made CATALOG. A pure lookup, exhaustive over
 * {@link BuilderChoice}.
 */
export function builderDestination(choice: BuilderChoice): BuilderDestination {
  return choice === 'custom' ? 'billing' : 'catalog';
}
